import { useEffect, useState } from "react";
import { Mail, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, asError } from "@/api/client";

export default function AdminContact() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/contact")
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Failed to load messages"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const mark = async (sub, status) => {
    try {
      await api.patch(`/admin/contact/${sub.id}/status`, null, { params: { status } });
      toast.success(`Marked as ${status}`);
      setItems((arr) => arr.map((x) => (x.id === sub.id ? { ...x, status } : x)));
    } catch (e) { toast.error(asError(e)); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const newCount = items.filter((x) => x.status === "new").length;

  return (
    <div data-testid="admin-contact">
      <div className="mb-5 text-sm text-[var(--ink-2)]">
        <span className="font-semibold text-[var(--ink)]">{items.length}</span> messages · <span className="font-semibold text-[var(--gold)]">{newCount}</span> unread
      </div>

      {loading && <div className="text-[var(--ink-3)] kbd text-xs">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="bg-white border border-[#e5dec9] rounded-xl p-12 text-center">
          <Mail size={32} className="mx-auto text-[var(--gold)]" />
          <p className="mt-3 text-[var(--ink-3)]">Inbox is empty.</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map((s) => (
          <article key={s.id} data-testid={`admin-contact-${s.id}`} className="bg-white border border-[#e5dec9] rounded-xl p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-serif-display text-lg text-[var(--ink)]">{s.name}</div>
                <a href={`mailto:${s.email}`} className="text-xs text-[var(--green)] hover:underline inline-flex items-center gap-1"><Mail size={11} /> {s.email}</a>
                <div className="text-[var(--ink-3)] text-xs mt-1">{fmt(s.created_at)}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full border ${
                  s.status === "new" ? "text-[var(--gold)] border-[var(--gold)] bg-amber-50" :
                  "text-[var(--green)] border-[var(--green)] bg-green-50"
                }`}>{s.status}</span>
                {s.status === "new" && (
                  <button onClick={() => mark(s, "resolved")} className="text-xs text-[var(--green)] inline-flex items-center gap-1 hover:underline">
                    <CheckCircle2 size={11} /> Mark resolved
                  </button>
                )}
              </div>
            </div>
            {s.subject && <div className="kbd text-[0.6rem] text-[var(--ink-3)] mt-3">Subject — <span className="text-[var(--ink-2)] normal-case font-medium tracking-normal">{s.subject}</span></div>}
            <p className="mt-2 text-sm text-[var(--ink-2)] leading-relaxed bg-[var(--bg-cream)] rounded-md p-3 whitespace-pre-line">{s.message}</p>
          </article>
        ))}
      </div>
    </div>
  );
}
